import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { SedsModalService } from "@serasa/shared/seds";

import { SiteSection } from '../../../../../shared/enums/site-section.enum';
import { AnalyticsService } from '../../../../../shared/services/analytics.service';
import { closeSideNav, loadLogonsList } from '../../state/actions/user.actions';
import { UserProfileChangeConfirmationComponent } from './confirmation/user-profile-change-confirmation.component';
import { selectLogon, selectUserMeInfo } from '../../state/selectors/user.selectors';
import { UserService } from '../../services/user.service';
import { ProfileFilterEnum } from '@serasa/core/modules/dashboard/components/search-form/components/filters/enums';
import { ErrorLoadingModalComponent } from '@serasa/core/modules/dashboard/components/error-loading-modal/error-loading-modal.component';
import { UserMe } from '@serasa/core/modules/recertification/interfaces/user-me.interface';
import { ProfileOption } from './models/profile-option.model';
import { ModalOptions } from './models/modal-options.model';
import { ModalData } from './models/modal-data.mode';
import { UserRequired } from './models/user-required.mode';

@Component({
    selector: 'pl-user-profile-change',
    templateUrl: './user-profile-change.component.html',
    styleUrls: ['./user-profile-change.component.scss'],
    standalone: false
})
export class UserProfileChangeComponent implements OnInit, OnDestroy {  
  public profileChangeForm: UntypedFormGroup;
  public currentUser: UserRequired = {} as UserRequired;
  public showSuccessAlert = false;
  public isLoading = false;
  public profileList: ProfileOption[] = [
    {
      key: 'USER',
      value: ProfileFilterEnum.USER,
    },
    {
      key: 'ACCESS_ADMIN',
      value: ProfileFilterEnum.ACCESS_ADMIN,
    }
  ];
  public userProfile = '';
  public lastSelectedProfile: ProfileOption | null = null;
  private readonly _subs: Subscription[] = [];
  public userDisplayText = '';
  
  public details$ = this.store.select(selectLogon);
  public userMeInfo$!: Observable<UserMe | null | undefined>;

  public get isActionDisabled(): boolean {
    return this.showSuccessAlert || this.isLoading;
  }
  
  constructor(
    private readonly store: Store,
    private readonly _analyticsService: AnalyticsService,
    private readonly _fb: UntypedFormBuilder,
    private readonly _modalService: SedsModalService,
    private readonly _userService: UserService
  ) {
    this.profileChangeForm = this._fb.group({
      logon: new UntypedFormControl('')
    });
  }
  
  ngOnInit() {
    this.showSuccessAlert = false;
    this._initializeData();
    this._analyticsService.doPageLoad(SiteSection.EDITAR_USUARIO);
  }
  
  private _initializeData(): void {
    this.userMeInfo$ = this.store.select(selectUserMeInfo);    
    this._subs.push(
      this.details$.subscribe(user => {
        if (user) {
          this.currentUser = user;
          this._createProfileChangeForm();
          this._updateUserDisplayText();
        }
      })
    );
  }

  public closeSideNav(): void {
    this.store.dispatch(closeSideNav());
  }  
  
  public save(): void {
    this.isLoading = true;
    const modalData: ModalData = {
      title: 'Tem certeza que deseja alterar o perfil deste usuário?',
      content: 'Ele passará a ter acesso ao Portal de Logons. Com isso, poderá gerenciar contas de outros usuários, incluindo: criar, excluir, bloquear, desbloquear, redefinir senhas e editar informações.',
    };
    
    const options: ModalOptions = {
      width: "550px",
    };
    
    if (!this.currentUser?.userId) {
      this._showErrorModal(options);
      return;
    }

    this._checkUserData(this.currentUser.logon || "", modalData, options);
  }

  private _checkUserData(logon: string, modalData: ModalData, options: ModalOptions): void {
    this._userService.getUserAccount(logon)
      .subscribe({
        next: (updatedUser) => {
          this.currentUser = updatedUser;
          if (this._validateRequiredFields(this.currentUser)) {
            this._proceedWithProfileChange(modalData, options);
          } else {
            this.isLoading = false;
            this._showErrorModal(options);
          }
        },
        error: () => {
          this.isLoading = false;
          this._showErrorModal(options);
        }
      });
  }


  private _validateRequiredFields(user: UserRequired): boolean {
    const requiredFields = ['userId', 'fullname'];
    const missingFields = requiredFields.filter(field => !user?.[field]);
    return missingFields.length === 0;
  }


  private _showErrorModal(options: ModalOptions): void {
    const modalDataError: ModalData = {
      title: 'Não foi possível alterar o perfil',
    };
    
    this._modalService.open(ErrorLoadingModalComponent, modalDataError, options);
  }

  private _proceedWithProfileChange(modalData: ModalData, options: ModalOptions): void {
    this._modalService.open(UserProfileChangeConfirmationComponent, modalData, options);
    this._modalService.close().subscribe(result => {      
      if (result === 'confirmed') {       
        const mainframeLogon = this.currentUser?.mainframeData?.[0]?.logon;
        const logon: string = mainframeLogon || this.currentUser?.['logon'] || '';
        this._subs.push(
          this._userService.updateUserProfile(logon, this.userProfile)            
          .subscribe({
              next: (response) => {
                this.showSuccessAlert = true;
                this.isLoading = false;
                this.store.dispatch(
                  loadLogonsList({
                    offset: 0,
                    limit: 20,
                    isPagination: false,
                  })
                );
              },
              error: (error) => {
                this.showSuccessAlert = false;
                this.isLoading = false;
              }
            })
        );
      } else {
        this.showSuccessAlert = false;
        this.isLoading = false;
      }
    });
  }
  
  private _createProfileChangeForm(): void {

    let selectedProfile = this.profileList.find(
      (profile: ProfileOption) => profile.key === this.currentUser.profile
    );

    if (!selectedProfile) {
      selectedProfile = this.profileList.find(
        (profile: ProfileOption) => profile.value === this.currentUser.profile
      );
    }

    this.userProfile = selectedProfile?.key || 'USER';
    this._updateUserDisplayText();
    this.profileChangeForm = this._fb.group({
      logon: new UntypedFormControl({ value: this.userDisplayText, disabled: true })
    });
  }


  public modelChange(selectedKey: string): void {
    this.showSuccessAlert = false;
    const selectedProfile = this.profileList.find(profile => profile.key === selectedKey) || null;
    this.userProfile = selectedKey;
    this.lastSelectedProfile = selectedProfile;
  }

  private _updateUserDisplayText(): void {
    const logon = this.currentUser?.logon?.toUpperCase() || '';
    const name = this.currentUser?.name || '';
    
    this.userDisplayText = logon && name 
      ? `${logon} | ${name}`
      : logon || '';
  }

  ngOnDestroy() {
    for (const sub of this._subs) {
      sub.unsubscribe();
    }
  }
}
